import React, { useCallback } from 'react';
import classNames from 'classnames';
import { addMonths, differenceInDays } from 'date-fns';
import { MonthPicker } from './MonthPicker';
import { fromYearMonthString, toMonthEndDate, toMonthStartDate } from './helpers';

export interface IMonthRangePickerProps {
  monthFrom?: string; // yyyy-MM
  monthTo?: string; // yyyy-MM
  onMonthRangeChange: (monthFrom?: string, monthTo?: string) => void;
  disabled?: boolean;
  periodFromMinYear?: number;
  periodFromMaxYear?: number;
  periodToMinYear?: number;
  periodToMaxYear?: number;
  maxPeriodInMonthsAllowed?: number; // defaults to 12
  labelFrom?: string;
  labelTo?: string;
  className?: string;
  eachMonthPickerWidth?: string;
  /** If true, appends '-01' to output strings (yyyy-MM becomes yyyy-MM-01) */
  outputAsFullDate?: boolean;
}

export const validateMonthRange = (
  range: { monthFrom?: string; monthTo?: string },
  maxPeriodInMonthsAllowed: number
): string | null => {
  // If range isn't fully selected yet, don't show validation.
  if (!range.monthFrom || !range.monthTo) {
    return null;
  }

  const from = fromYearMonthString(range.monthFrom);
  const to = fromYearMonthString(range.monthTo);

  // If either side is malformed, treat as invalid but don't hard-crash.
  if (!from || !to) {
    return 'Please select a valid period.';
  }

  const fromDate = toMonthStartDate(from);
  const toDate = toMonthEndDate(to);

  const diffInDays = differenceInDays(toDate, fromDate);
  if (diffInDays < 0) {
    return 'End date must be after start date.';
  }

  const maxToDate = addMonths(fromDate, maxPeriodInMonthsAllowed);
  const maxAllowedDiffInDays = differenceInDays(maxToDate, fromDate);
  if (diffInDays > maxAllowedDiffInDays) {
    return `Please select a period of up to ${maxPeriodInMonthsAllowed} months.`;
  }

  return null;
};

export const MonthRangePicker: React.FC<IMonthRangePickerProps> = ({
  monthFrom,
  monthTo,
  onMonthRangeChange,
  disabled = false,
  periodFromMinYear,
  periodFromMaxYear,
  periodToMinYear,
  periodToMaxYear,
  maxPeriodInMonthsAllowed = 12,
  eachMonthPickerWidth = '152px',
  labelFrom = 'Period From',
  labelTo = 'Period To',
  className,
  outputAsFullDate = false,
}) => {
  const formatOutput = useCallback((value?: string) => {
    if (!value) return value;
    return outputAsFullDate ? `${value}-01` : value;
  }, [outputAsFullDate]);

  // Strip '-01' suffix from input if outputAsFullDate is true (for internal yyyy-MM format)
  const normalizeInput = useCallback((value?: string) => {
    if (!value) return value;
    return outputAsFullDate && value.endsWith('-01') ? value.slice(0, -3) : value;
  }, [outputAsFullDate]);

  const internalMonthFrom = normalizeInput(monthFrom);
  const internalMonthTo = normalizeInput(monthTo);

  const handleMonthRangeChange = useCallback(
    (nextMonthFrom?: string, nextMonthTo?: string) => {
      onMonthRangeChange(formatOutput(nextMonthFrom), formatOutput(nextMonthTo));
    },
    [onMonthRangeChange, formatOutput]
  );

  const handleFromChange = useCallback(
    (nextFrom: string) => {
      handleMonthRangeChange(nextFrom, internalMonthTo);
    },
    [handleMonthRangeChange, internalMonthTo]
  );

  const handleToChange = useCallback(
    (nextTo: string) => {
      handleMonthRangeChange(internalMonthFrom, nextTo);
    },
    [handleMonthRangeChange, internalMonthFrom]
  );

  const errorMessage = validateMonthRange({ monthFrom: internalMonthFrom, monthTo: internalMonthTo }, maxPeriodInMonthsAllowed);
  const showError = errorMessage != null && errorMessage.length > 0;

  return (
    <div className={classNames('month-range-picker', className)}>
      <div className="month-pickers-container flex gap-[5px] items-end">
        <div style={{ minWidth: eachMonthPickerWidth }}>
          <MonthPicker
            label={labelFrom}
            value={internalMonthFrom}
            onChange={handleFromChange}
            minYear={periodFromMinYear}
            maxYear={periodFromMaxYear}
            dropdownAlignment="left"
            dropdownClassName="w-[150px]"
            disabled={disabled}
          />
        </div>
        <div style={{ minWidth: eachMonthPickerWidth }}>
          <MonthPicker
            label={labelTo}
            value={internalMonthTo}
            onChange={handleToChange}
            minYear={periodToMinYear}
            maxYear={periodToMaxYear}
            dropdownAlignment="right"
            dropdownClassName="w-[150px]"
            disabled={disabled}
            inputClassName={classNames({
              'border-red-95 bg-red-25': showError,
            })}
          />
        </div>
      </div>

      {showError && (
        <p className="month-range-picker-error-message m-0 mt-[5px] font-pt-sans text-[13px] leading-[17px] text-red-95">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
